import { useState } from 'react';

const BeforeAfter = () => {
  const [activeTab, setActiveTab] = useState<'before' | 'after'>('before');

  const beforeData = {
    title: 'Without MTAPLANNERPRO',
    subtitle: 'The Old Way of Quoting',
    color: 'from-red-50 to-orange-50',
    borderColor: 'border-red-200',
    iconColor: 'text-red-600',
    stats: [
      {
        icon: 'ri-time-line',
        metric: '3-5 Hours',
        label: 'Per Quote',
        description: 'Manual counting and calculations eat up your entire afternoon'
      },
      {
        icon: 'ri-calculator-line',
        metric: '15-30%',
        label: 'Error Rate',
        description: 'Manual take-offs lead to costly mistakes and margin loss'
      },
      {
        icon: 'ri-file-paper-line',
        metric: 'Handwritten',
        label: 'Quote Format',
        description: 'Unprofessional quotes that don\'t win jobs'
      },
      {
        icon: 'ri-question-line',
        metric: 'Guesswork',
        label: 'Margin Calculation',
        description: 'No clear visibility on profit until the job is done'
      }
    ]
  };

  const afterData = {
    title: 'With MTAPLANNERPRO',
    subtitle: 'The Smart Way to Quote',
    color: 'from-emerald-50 to-teal-50',
    borderColor: 'border-emerald-200',
    iconColor: 'text-emerald-600',
    stats: [
      {
        icon: 'ri-flashlight-line',
        metric: '5 Minutes',
        label: 'Per Quote',
        description: 'AI-powered automation generates quotes while you grab a coffee'
      },
      {
        icon: 'ri-check-double-line',
        metric: '99%',
        label: 'Accuracy',
        description: 'AI eliminates human error and ensures precise material counts'
      },
      {
        icon: 'ri-file-pdf-line',
        metric: 'Branded PDF',
        label: 'Quote Format',
        description: 'Professional quotes with your logo, ABN, and payment terms'
      },
      {
        icon: 'ri-line-chart-line',
        metric: 'Real-Time',
        label: 'Profit Tracking',
        description: 'Built-in margin calculator shows your profit before you quote'
      }
    ]
  };

  const comparisonMetrics = [
    {
      label: 'Time Per Quote',
      before: '3-5 hours',
      after: '5 minutes',
      improvement: '95% faster'
    },
    {
      label: 'Quote Accuracy',
      before: '70-85%',
      after: '99%',
      improvement: '14-29% better'
    },
    {
      label: 'Weekly Time Saved',
      before: '0 hours',
      after: '15-20 hours',
      improvement: '20+ hours back'
    },
    {
      label: 'Professional Presentation',
      before: 'Handwritten/Basic',
      after: 'Branded PDF',
      improvement: '3x more wins'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 via-white to-amber-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
            <i className="ri-contrast-2-line text-amber-600"></i>
            <span className="text-sm font-semibold text-amber-900">The Transformation</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Stop Wasting Time on Manual Quotes
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            See how MTAPLANNERPRO transforms your quoting process from hours of manual work to minutes of automated precision
          </p>
        </div>

        {/* Mobile Toggle */}
        <div className="flex justify-center mb-12 lg:hidden">
          <div className="inline-flex bg-white rounded-full p-1 shadow-lg border border-slate-200">
            <button
              onClick={() => setActiveTab('before')}
              className={`px-6 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap ${
                activeTab === 'before'
                  ? 'bg-red-500 text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <i className="ri-close-circle-line mr-2"></i>
              Without
            </button>
            <button
              onClick={() => setActiveTab('after')}
              className={`px-6 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap ${
                activeTab === 'after'
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <i className="ri-check-line mr-2"></i>
              With MTAPLANNERPRO
            </button>
          </div>
        </div>

        {/* Split Screen Comparison - Desktop */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-8 mb-20">
          {/* Before Column */}
          <div className={`bg-gradient-to-br ${beforeData.color} rounded-2xl p-8 border-2 ${beforeData.borderColor} relative`}>
            <div className="absolute top-4 right-4">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                <i className="ri-close-line text-2xl text-white"></i>
              </div>
            </div>
            
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-slate-900 mb-2">{beforeData.title}</h3>
              <p className="text-slate-600 font-medium">{beforeData.subtitle}</p>
            </div>

            <div className="space-y-6">
              {beforeData.stats.map((stat, index) => (
                <div key={index} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-red-100">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${beforeData.iconColor} bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <i className={`${stat.icon} text-2xl`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="text-2xl font-bold text-slate-900 mb-1">{stat.metric}</div>
                      <div className="text-sm font-semibold text-slate-700 mb-2">{stat.label}</div>
                      <p className="text-sm text-slate-600">{stat.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* After Column */}
          <div className={`bg-gradient-to-br ${afterData.color} rounded-2xl p-8 border-2 ${afterData.borderColor} relative shadow-xl`}>
            <div className="absolute top-4 right-4">
              <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-2xl text-white"></i>
              </div>
            </div>
            
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-slate-900 mb-2">{afterData.title}</h3>
              <p className="text-slate-600 font-medium">{afterData.subtitle}</p>
            </div>

            <div className="space-y-6">
              {afterData.stats.map((stat, index) => (
                <div key={index} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-emerald-100">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${afterData.iconColor} bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <i className={`${stat.icon} text-2xl`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="text-2xl font-bold text-slate-900 mb-1">{stat.metric}</div>
                      <div className="text-sm font-semibold text-slate-700 mb-2">{stat.label}</div>
                      <p className="text-sm text-slate-600">{stat.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile View */}
        <div className="lg:hidden mb-20">
          {activeTab === 'before' ? (
            <div className={`bg-gradient-to-br ${beforeData.color} rounded-2xl p-6 border-2 ${beforeData.borderColor}`}>
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{beforeData.title}</h3>
                <p className="text-slate-600 font-medium">{beforeData.subtitle}</p>
              </div>
              <div className="space-y-4">
                {beforeData.stats.map((stat, index) => (
                  <div key={index} className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-red-100">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 ${beforeData.iconColor} bg-white rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <i className={`${stat.icon} text-xl`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="text-xl font-bold text-slate-900 mb-1">{stat.metric}</div>
                        <div className="text-sm font-semibold text-slate-700 mb-1">{stat.label}</div>
                        <p className="text-sm text-slate-600">{stat.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`bg-gradient-to-br ${afterData.color} rounded-2xl p-6 border-2 ${afterData.borderColor} shadow-xl`}>
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{afterData.title}</h3>
                <p className="text-slate-600 font-medium">{afterData.subtitle}</p>
              </div>
              <div className="space-y-4">
                {afterData.stats.map((stat, index) => (
                  <div key={index} className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-emerald-100">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 ${afterData.iconColor} bg-white rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <i className={`${stat.icon} text-xl`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="text-xl font-bold text-slate-900 mb-1">{stat.metric}</div>
                        <div className="text-sm font-semibold text-slate-700 mb-1">{stat.label}</div>
                        <p className="text-sm text-slate-600">{stat.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Comparison Metrics Table */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-8 py-6">
            <h3 className="text-2xl font-bold text-white text-center">Side-by-Side Comparison</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">Metric</th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-red-600">
                    <i className="ri-close-circle-line mr-2"></i>
                    Without
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-emerald-600">
                    <i className="ri-check-line mr-2"></i>
                    With MTAPLANNERPRO
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-amber-600">
                    <i className="ri-arrow-up-line mr-2"></i>
                    Improvement
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonMetrics.map((metric, index) => (
                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-5 font-semibold text-slate-900">{metric.label}</td>
                    <td className="px-6 py-5 text-center text-slate-600">{metric.before}</td>
                    <td className="px-6 py-5 text-center font-semibold text-emerald-600">{metric.after}</td>
                    <td className="px-6 py-5 text-center">
                      <span className="inline-flex items-center px-3 py-1 bg-amber-100 text-amber-900 rounded-full text-sm font-bold whitespace-nowrap">
                        <i className="ri-arrow-up-line mr-1"></i>
                        {metric.improvement}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-8 shadow-2xl">
            <div className="text-left">
              <h4 className="text-2xl font-bold text-white mb-2">Ready to Transform Your Quoting?</h4>
              <p className="text-amber-50">Join 500+ Australian tradies saving 15+ hours per week</p>
            </div>
            <button className="px-8 py-4 bg-white text-amber-600 rounded-xl font-bold hover:bg-amber-50 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap">
              Start Free Trial
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-4">No credit card required • 14-day free trial • Cancel anytime</p>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfter;